import { useState } from "react";
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  parseISO
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import type { Task } from "@/hooks/use-tasks";

interface SimpleCalendarProps {
  tasks: Task[] | undefined;
  onDateSelect: (dateStr: string, tasks: Task[]) => void;
}

export function SimpleCalendar({ tasks, onDateSelect }: SimpleCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Process tasks
  const tasksByDate: Record<string, Task[]> = {};
  if (Array.isArray(tasks)) {
    tasks.forEach(task => {
      if (task.dueDate) {
        // Handle ISO strings correctly
        const dateStr = task.dueDate.split('T')[0]; // Format: YYYY-MM-DD
        if (!tasksByDate[dateStr]) {
          tasksByDate[dateStr] = [];
        }
        tasksByDate[dateStr].push(task);
      }
    });
  }

  const onDateClick = (day: Date) => {
    setSelectedDate(day);
    const dateStr = format(day, 'yyyy-MM-dd');
    onDateSelect(dateStr, tasksByDate[dateStr] || []);
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  // Header row with day names
  const renderDays = () => {
    const days = [];
    const dateFormat = "E";
    const startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      const day = addDays(startDate, i);
      days.push(
        <div className="text-center font-medium text-gray-500 text-xs sm:text-sm" key={i}>
          {format(day, dateFormat)}
        </div>
      );
    }

    return <div className="grid grid-cols-7 mb-2">{days}</div>;
  };

  // Calendar cells 
  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayTasks = tasksByDate[dateStr] || [];
        const hasTask = dayTasks.length > 0;
        
        const formattedDate = format(day, 'd');
        
        days.push(
          <div
            key={dateStr}
            className={`min-h-[40px] p-1 flex flex-col items-center justify-center relative border ${
              !isSameMonth(day, monthStart) 
                ? 'text-gray-400 bg-gray-50' 
                : isSameDay(day, selectedDate) 
                  ? 'bg-primary text-white font-bold'
                  : isSameDay(day, new Date()) 
                    ? 'border-primary' 
                    : 'hover:bg-gray-100'
            }`}
            onClick={() => onDateClick(day)}
          >
            <span className="text-lg sm:text-base font-semibold">{formattedDate}</span>
            {hasTask && (
              <div 
                className={`mt-1 w-2 h-2 rounded-full ${
                  isSameDay(day, selectedDate) ? 'bg-white' : 'bg-primary'
                }`} 
              />
            )}
          </div>
        );
        
        day = addDays(day, 1);
      }
      
      rows.push(
        <div className="grid grid-cols-7 gap-1" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    
    return <div className="mb-2">{rows}</div>;
  };

  return (
    <div className="p-3 bg-white">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="font-bold text-lg">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          onClick={nextMonth}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      
      {renderDays()}
      {renderCells()}
      
      <div className="pt-4 border-t mt-2">
        <div className="flex items-center text-sm text-gray-500">
          <div className="w-3 h-3 rounded-full bg-primary mr-2"></div>
          <span>Days with tasks</span>
        </div>
      </div>
    </div>
  );
}
